import React, { useState } from 'react';
import { ShieldCheck, Eye, EyeOff, FileText, Calendar, HardDrive } from 'lucide-react';
import { formatFileSize } from '../../utils/fileSize';

interface ExifViewerProps {
  file: File | null;
  dimensions?: { width: number; height: number } | null;
}

export function ExifViewer({ file, dimensions }: ExifViewerProps) {
  const [stripExif, setStripExif] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  if (!file) return null;

  const modifiedDate = new Date(file.lastModified).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm space-y-3">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-emerald-500" />
          <h4 className="text-xs font-semibold text-slate-800 dark:text-slate-200">
            Privacy & Metadata Control
          </h4>
        </div>
        <button
          type="button"
          onClick={() => setShowDetails(!showDetails)}
          className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1 font-medium"
        >
          {showDetails ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          {showDetails ? 'Hide Metadata' : 'View File Metadata'}
        </button>
      </div>

      <div className="flex items-center justify-between p-2.5 rounded-lg bg-emerald-50/70 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50 text-xs">
        <div className="flex items-center gap-2 text-emerald-900 dark:text-emerald-300">
          <input
            type="checkbox"
            id="strip-exif-toggle"
            checked={stripExif}
            onChange={(e) => setStripExif(e.target.checked)}
            className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
          />
          <label htmlFor="strip-exif-toggle" className="font-medium cursor-pointer">
            Automatically strip EXIF GPS & Camera metadata on export
          </label>
        </div>
        <span className="text-[11px] font-bold text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/60 px-2 py-0.5 rounded">
          Privacy Active
        </span>
      </div>

      {showDetails && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-600 dark:text-slate-400">
          <div className="p-2 rounded bg-slate-50 dark:bg-slate-800/50">
            <span className="text-[10px] text-slate-400 block uppercase font-medium flex items-center gap-1">
              <FileText className="w-3 h-3" /> File Name
            </span>
            <span className="font-semibold text-slate-800 dark:text-slate-200 truncate block mt-0.5">
              {file.name}
            </span>
          </div>

          <div className="p-2 rounded bg-slate-50 dark:bg-slate-800/50">
            <span className="text-[10px] text-slate-400 block uppercase font-medium flex items-center gap-1">
              <HardDrive className="w-3 h-3" /> Original Size
            </span>
            <span className="font-semibold text-slate-800 dark:text-slate-200 block mt-0.5">
              {formatFileSize(file.size)}
            </span>
          </div>

          <div className="p-2 rounded bg-slate-50 dark:bg-slate-800/50">
            <span className="text-[10px] text-slate-400 block uppercase font-medium">Resolution</span>
            <span className="font-semibold text-slate-800 dark:text-slate-200 block mt-0.5 font-mono">
              {dimensions ? `${dimensions.width} × ${dimensions.height} px` : 'Loading...'}
            </span>
          </div>

          <div className="p-2 rounded bg-slate-50 dark:bg-slate-800/50">
            <span className="text-[10px] text-slate-400 block uppercase font-medium flex items-center gap-1">
              <Calendar className="w-3 h-3" /> Date Modified
            </span>
            <span className="font-semibold text-slate-800 dark:text-slate-200 block mt-0.5 truncate">
              {modifiedDate}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
